import React, { useState, useRef, useEffect } from 'react';

/**
 * Option interface for dropdown items
 * @property value - The value to be used when option is selected
 * @property label - The text to display for the option
 */
interface Option {
  value: string;
  label: string;
  color?: string;
}

/**
 * Props for the CustomDropdown component
 * @property id - Unique identifier for the dropdown
 * @property name - Form field name
 * @property label - Optional label text above the dropdown
 * @property value - Currently selected value
 * @property placeholder - Text to show when no option is selected
 * @property onChange - Callback function when selection changes
 * @property options - Array of available options
 * @property className - Additional CSS classes
 * @property required - Whether the field is required
 * @property disabled - Whether the dropdown is disabled
 * @property error - Error message to display
 * @property size - Size variant (sm, md, lg)
 * @property showAsterisk - Whether to show the asterisk
 */
interface CustomDropdownProps {
  id: string;
  name: string;
  label?: string;
  value?: string;
  placeholder?: string;
  onChange: (value: string) => void;
  options: Option[];
  className?: string;
  required?: boolean;
  showAsterisk?: boolean;
  disabled?: boolean;
  error?: string;
  size?: 'sm' | 'md' | 'lg';
}

/**
 * CustomDropdown Component
 * 
 * A reusable dropdown component that provides:
 * - Custom styling with Tailwind CSS
 * - Dark mode support
 * - Accessibility features
 * - Multiple size variants
 * - Error state handling
 * - Required field indication
 * - Disabled state support
 * - Click outside to close
 * - Empty option support
 */
const CustomDropdown: React.FC<CustomDropdownProps> = ({
  id,
  name,
  label,
  value,
  placeholder = 'Select an option',
  onChange,
  options,
  className = '',
  required = false,
  showAsterisk = false,
  disabled = false,
  error,
  size = 'md',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropUpwards, setDropUpwards] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Get currently selected option for display
  const selectedOption = options.find(opt => opt.value === value);

  /**
   * Handles clicks outside the dropdown to close it
   * Adds and removes event listener on mount/unmount
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    // Only add the listener when the dropdown is open
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]); // Add isOpen to dependency array

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const spaceBelow = windowHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;
      const dropdownHeight = 600; 

      setDropUpwards(spaceBelow < dropdownHeight && spaceAbove > spaceBelow);
    }
  }, [isOpen]);

  /**
   * Returns appropriate size-based classes
   * @returns Tailwind CSS classes for sizing
   */
  const getSizeClasses = () => {
    switch (size) {
      case 'sm': return 'p-1.5 text-sm';
      case 'lg': return 'p-3 text-lg';
      default: return 'p-2.5 text-sm';
    }
  };

  /**
   * Handles option selection
   * Updates parent component and closes dropdown
   * @param option - The selected option
   */
  const handleSelect = (option: Option) => {
    onChange(option.value);
    setIsOpen(false);
  };

  /**
   * Base button styles including:
   * - Conditional styling for states (error, disabled)
   * - Dark mode variants
   * - Focus and hover states
   */
  const baseButtonStyles = `
    w-full text-left rounded-lg border 
    ${error ? 'border-red-500' : 'border-gray-300'} 
    ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-gray-50'} 
    text-gray-900
    focus:ring-blue-500 focus:border-blue-500 
    dark:bg-slate-800 dark:border-gray-700 
    dark:placeholder-gray-400 dark:text-white 
    dark:focus:ring-blue-500 dark:focus:border-blue-500
    ${getSizeClasses()}
  `;

  return (
    <div className="mb-4">
      {label && (
        <label htmlFor={id} className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
          {label}
          {showAsterisk && required && '*'}
        </label>
      )}
      <div className="mt-2">
        <div className="relative" ref={dropdownRef}>
          {/* Main dropdown button */}
          <button
            ref={buttonRef}
            type="button"
            id={id}
            className={`${baseButtonStyles} ${className}`}
            onClick={() => !disabled && setIsOpen(!isOpen)}
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 truncate">
                {selectedOption?.color && (
                  <div 
                    className="w-3 h-3 rounded-full flex-shrink-0" 
                    style={{ backgroundColor: selectedOption.color }}
                  />
                )}
                <span className="truncate">
                  {selectedOption ? selectedOption.label : placeholder}
                </span>
              </div>
              {/* Animated dropdown arrow */}
              <svg 
                className={`w-4 h-4 ml-2 transition-transform duration-200 ${isOpen ? 'transform rotate-180' : ''}`}
                aria-hidden="true" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </div>
          </button>

          {/* Dropdown menu with conditional positioning */}
          {isOpen && (
            <div 
              className={`absolute z-[100] w-full ${
                dropUpwards ? 'bottom-full mb-1' : 'top-full mt-1'
              } bg-gray-50 dark:bg-[#374151] shadow-lg rounded-lg overflow-hidden`}
            >
              <ul
                className="py-1 text-sm text-gray-900 dark:text-gray-200 max-h-[420px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent"
                role="listbox"
                aria-labelledby={id}
              >
                {/* Empty option for clearing selection */}
                <li
                  className={`
                    ${!value ? 'bg-gray-200 dark:bg-[#4b5563]' : ''} 
                    px-4 py-2 cursor-pointer
                    hover:bg-gray-200 dark:hover:bg-[#4b5563]
                    text-gray-900 dark:text-gray-200
                    transition-colors duration-150 ease-in-out
                  `}
                  onClick={() => handleSelect({ value: '', label: placeholder })}
                  role="option"
                  aria-selected={!value}
                >
                  {placeholder}
                </li>

                {/* Option items */}
                {options.map((option) => (
                  <li
                    key={option.value}
                    className={`
                      ${option.value === value ? 'bg-gray-200 dark:bg-[#4b5563]' : ''}
                      px-4 py-2 cursor-pointer
                      hover:bg-gray-200 dark:hover:bg-[#4b5563]
                      text-gray-900 dark:text-gray-200
                      transition-colors duration-150 ease-in-out
                    `}
                    onClick={() => handleSelect(option)}
                    role="option"
                    aria-selected={option.value === value}
                  >
                    <div className="flex items-center gap-2">
                      {option.color && (
                        <div 
                          className="w-3 h-3 rounded-full flex-shrink-0" 
                          style={{ backgroundColor: option.color }}
                        />
                      )}
                      <span className="truncate">{option.label}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Error message display */}
      {error && (
        <p className="mt-1 text-sm text-red-500">
          {error}
        </p>
      )}
    </div>
  );
};

export default CustomDropdown; 